import React, { useState, useEffect } from 'react';
import API_BASE from './apiBase';
import axios from 'axios';
import Swal from 'sweetalert2';

const COMPANY_NAME    = process.env.REACT_APP_COMPANY_NAME    || 'Whitehats Technology Pvt. Ltd.';
const COMPANY_ADDRESS = process.env.REACT_APP_COMPANY_ADDRESS || '123, Tech Park, Sector 62, Noida, Uttar Pradesh – 201301';
const COMPANY_GST     = process.env.REACT_APP_COMPANY_GST     || '09AABCW1234A1Z5';
const COMPANY_PAN     = process.env.REACT_APP_COMPANY_PAN     || 'AABCW1234A';
const COMPANY_EMAIL   = process.env.REACT_APP_COMPANY_EMAIL   || 'billing@whitehatstech.com';
const COMPANY_PHONE   = process.env.REACT_APP_COMPANY_PHONE   || '+91-98765-43210';

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
const GST_RATE = 0.18;

// ── Load Razorpay script ──────────────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise((resolve) => {
    // Already loaded
    if (window.Razorpay) { resolve(true); return; }
    // Script tag already injected, wait for it
    const existing = document.getElementById('razorpay-script');
    if (existing) {
      existing.onload = () => resolve(true);
      existing.onerror = () => resolve(false);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function addMonths(dateStr, months) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function SubscriptionModal({ show, onClose, user }) {
  const [organization, setOrganization]     = useState('');
  const [email, setEmail]                   = useState('');
  const [plan, setPlan]                     = useState('Subscription');
  const [selectedUsers, setSelectedUsers]   = useState(1);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [startDate, setStartDate]           = useState(todayISO());
  const [endDate, setEndDate]               = useState('');
  const [submitting, setSubmitting]         = useState(false);
  const [step, setStep]                     = useState('form');

  const [billingName, setBillingName]       = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingGST, setBillingGST]         = useState('');
  const [billingPAN, setBillingPAN]         = useState('');

  const tier1Rate = parseInt(process.env.REACT_APP_TIER1_RATE || '299', 10);
  const tier2Rate = parseInt(process.env.REACT_APP_TIER2_RATE || '249', 10);
  const tier3Rate = parseInt(process.env.REACT_APP_TIER3_RATE || '225', 10);

  function rateForUsers(count) {
    if (count <= 5)  return tier1Rate;
    if (count <= 15) return tier2Rate;
    return tier3Rate;
  }

  const baseAmt  = rateForUsers(selectedUsers) * selectedUsers * selectedMonths;
  const gstAmt   = parseFloat((baseAmt * GST_RATE).toFixed(2));
  const totalAmt = parseFloat((baseAmt + gstAmt).toFixed(2));

  // Reset step when modal opens
  useEffect(() => {
    if (show) setStep('form');
  }, [show]);

  // Auto end date
  useEffect(() => {
    if (startDate) setEndDate(addMonths(startDate, selectedMonths));
  }, [startDate, selectedMonths]);

  // Pre-fill from user / localStorage
  useEffect(() => {
    try {
      const tname = localStorage.getItem('tenant_name');
      if (tname) { setOrganization(tname); setBillingName(tname); }
      const taddr = localStorage.getItem('tenant_address');
      if (taddr) setBillingAddress(taddr);
      const tgst = localStorage.getItem('tenant_gst');
      if (tgst) setBillingGST(tgst);
      const tpan = localStorage.getItem('tenant_pan');
      if (tpan) setBillingPAN(tpan);
    } catch (e) {}
    if (user?.email)        setEmail(user.email);
    if (user?.organization) { setOrganization(user.organization); setBillingName(user.organization); }
    if (user?.address)      setBillingAddress(user.address);
    if (user?.gst_number)   setBillingGST(user.gst_number);
    if (user?.pan_number)   setBillingPAN(user.pan_number);
    if (user?.preSelectedUsers  > 0) setSelectedUsers(user.preSelectedUsers);
    if (user?.preSelectedMonths > 0) setSelectedMonths(user.preSelectedMonths);
  }, [user]);

  if (!show) return null;

  // ── MAIN FIX: proper async flow, setSubmitting(false) only on error/dismiss ──
  const handleConfirmPayment = async () => {
    setSubmitting(true);

    try {
      // 1. Load Razorpay SDK
      const loaded = await loadRazorpayScript();
      if (!loaded || !window.Razorpay) {
        Swal.fire({ icon: 'error', title: 'SDK Error', text: 'Razorpay checkout could not load. Check your internet connection and try again.' });
        setSubmitting(false);
        return;
      }

      // 2. Create order on backend
      const amountPaise = Math.round(totalAmt * 100);
      const meta = {
        organization, email, plan,
        users: selectedUsers, months: selectedMonths,
        start_date: startDate, end_date: endDate,
        billing_name: billingName, billing_address: billingAddress,
        billing_gst: billingGST, billing_pan: billingPAN,
        base_amount: baseAmt, gst_amount: gstAmt, total_amount: totalAmt,
      };

      let orderId, keyId;
      try {
        const res = await axios.post(`${API_BASE}/payments/create-order`, {
          amount: amountPaise,
          currency: 'INR',
          item_name: `${plan} - ${organization || 'Subscription'}`,
          metadata: meta,
        });
        orderId = res.data?.order_id;
        keyId   = res.data?.key_id;
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Order creation failed';
        Swal.fire({ icon: 'error', title: 'Order Failed', text: String(msg) });
        setSubmitting(false);
        return;
      }

      if (!orderId) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Payment order ID not received from server.' });
        setSubmitting(false);
        return;
      }

      // 3. Open Razorpay checkout
      // NOTE: Do NOT call setSubmitting(false) here — keep button disabled until
      // user completes/dismisses the Razorpay popup
      const options = {
        key: RAZORPAY_KEY_ID || keyId || '',
        amount: amountPaise,
        currency: 'INR',
        name: COMPANY_NAME,
        description: `${plan} — ${selectedUsers} user(s) × ${selectedMonths} month(s)`,
        image: process.env.REACT_APP_LOGO_URL || '',
        order_id: orderId,
        prefill: {
          name: billingName || organization,
          email: email,
          contact: '',
        },
        notes: {
          organization,
          plan,
          users: String(selectedUsers),
          months: String(selectedMonths),
          gst_number: billingGST,
          pan: billingPAN,
          address: billingAddress,
        },
        theme: { color: '#4777e8' },

        // Called when payment succeeds inside Razorpay popup
        handler: async function (response) {
          // Popup closed by Razorpay after success — now verify
          try {
            await axios.post(`${API_BASE}/payments/verify`, {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              metadata: meta,
            });
            setSubmitting(false);
            Swal.fire({
              icon: 'success',
              title: '🎉 Payment Successful!',
              text: `Subscription activated for ${organization}. Invoice will be sent to ${email}.`,
            });
            if (typeof onClose === 'function') onClose();
          } catch (verifyErr) {
            setSubmitting(false);
            Swal.fire({
              icon: 'warning',
              title: 'Verification Pending',
              text: 'Payment was received but verification is pending. Our team will confirm shortly.',
            });
            if (typeof onClose === 'function') onClose();
          }
        },

        modal: {
          // User closed popup without paying — re-enable button
          ondismiss: function () {
            setSubmitting(false);
          },
          // Keep backdrop, don't close on outside click accidentally
          escape: true,
          backdropclose: false,
        },
      };

      const rzp = new window.Razorpay(options);

      // Payment failed inside Razorpay popup
      rzp.on('payment.failed', function (response) {
        setSubmitting(false);
        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: response.error?.description || response.error?.reason || 'Payment could not be completed. Please try again.',
        });
      });

      // Open the popup — this is non-blocking, handler/ondismiss handle the rest
      rzp.open();

    } catch (err) {
      // Unexpected error
      setSubmitting(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.detail || err.message || 'Something went wrong. Please try again.',
      });
    }
  };

  /* ─── Styles ─────────────────────────────────── */
  const S = {
    overlay: { position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(80,106,239,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2000 },
    modal:   { background:'#fff',borderRadius:12,width:700,maxWidth:'96%',maxHeight:'92vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.25)',fontFamily:"'Segoe UI',sans-serif" },
    header:  { background:'#4777e8',color:'#fff',padding:'18px 24px',borderRadius:'12px 12px 0 0',display:'flex',alignItems:'center',justifyContent:'space-between' },
    hTitle:  { margin:0,fontSize:20,fontWeight:700 },
    body:    { padding:'18px 24px' },
    secHd:   { fontSize:11,fontWeight:700,color:'#2a3a5e',textTransform:'uppercase',letterSpacing:1.2,borderBottom:'2px solid #e8ecf4',paddingBottom:6,marginBottom:12,marginTop:18 },
    lbl:     { display:'block',fontSize:12,color:'#555',marginBottom:4,fontWeight:600 },
    inp:     { width:'100%',padding:'9px 12px',border:'1px solid #dde1ea',borderRadius:6,fontSize:14,boxSizing:'border-box',background:'#fafbfd',color:'#222' },
    sel:     { width:'100%',padding:'9px 12px',border:'1px solid #dde1ea',borderRadius:6,fontSize:14,background:'#fafbfd',color:'#222' },
    row:     { display:'flex',gap:12,marginBottom:12 },
    col:     { flex:1 },
    dateBox: { background:'#eef2ff',border:'1px solid #c5cff0',borderRadius:6,padding:'9px 12px',fontSize:14,color:'#2a3a5e',fontWeight:600 },
    footer:  { padding:'16px 24px',borderTop:'1px solid #eee',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fafbfd',borderRadius:'0 0 12px 12px' },
    btnSec:  { padding:'9px 20px',border:'1px solid #ccc',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:14,color:'#333' },
    btnPri:  { padding:'10px 24px',border:'none',borderRadius:6,background:'#4777e8',color:'#fff',cursor:'pointer',fontSize:14,fontWeight:700,opacity:1,transition:'opacity 0.2s' },
    btnDis:  { padding:'10px 24px',border:'none',borderRadius:6,background:'#a0b4f0',color:'#fff',cursor:'not-allowed',fontSize:14,fontWeight:700 },
    badge:   { display:'inline-block',background:'#e8f0fe',color:'#517fea',border:'1px solid #b0c0e8',borderRadius:4,padding:'2px 8px',fontSize:12,fontWeight:600,marginRight:6,marginTop:4 },
    compCard:{ background:'#f7f9ff',border:'1px solid #d4daf0',borderRadius:8,padding:'14px 16px',marginBottom:16,fontSize:13,color:'#333' },
  };

  /* ─── FORM STEP ──────────────────────────────── */
  if (step === 'form') return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <h2 style={S.hTitle}>📋 Create Subscription</h2>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#fff',fontSize:22,cursor:'pointer'}}>✕</button>
        </div>
        <div style={S.body}>
          <div style={S.secHd}>Subscription Details</div>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.lbl}>Organization / Tenant</label>
              <input value={organization} onChange={e=>setOrganization(e.target.value)} placeholder="Organization name" style={S.inp} />
            </div>
            <div style={S.col}>
              <label style={S.lbl}>Contact Email</label>
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@company.com" style={S.inp} />
            </div>
          </div>
          <div style={S.row}>
            <div style={{flex:2}}>
              <label style={S.lbl}>Plan</label>
              <select value={plan} onChange={e=>setPlan(e.target.value)} style={S.sel}>
                <option value="Subscription">Subscription</option>
                <option value="Pay as You Go">Pay as You Go</option>
              </select>
            </div>
            <div style={{flex:1}}>
              <label style={S.lbl}>No. of Users</label>
              <input type="number" min={1} value={selectedUsers}
                onChange={e=>setSelectedUsers(Math.max(1,parseInt(e.target.value||'1',10)))} style={S.inp} />
            </div>
            <div style={{flex:1}}>
              <label style={S.lbl}>Duration</label>
              <select value={selectedMonths} onChange={e=>setSelectedMonths(parseInt(e.target.value,10))} style={S.sel}>
                <option value={1}>1 Month</option>
                <option value={6}>6 Months</option>
                <option value={12}>12 Months</option>
              </select>
            </div>
          </div>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.lbl}>Start Date</label>
              <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={S.inp} />
            </div>
            <div style={S.col}>
              <div style={{...S.lbl,color:'#888'}}>End Date (auto-calculated)</div>
              <div style={S.dateBox}>{formatDateDisplay(endDate) || '—'}</div>
            </div>
          </div>

          <div style={S.secHd}>Billing / GST Details</div>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.lbl}>Billing Name</label>
              <input value={billingName} onChange={e=>setBillingName(e.target.value)} placeholder="Company or individual name" style={S.inp} />
            </div>
            <div style={S.col}>
              <label style={S.lbl}>GST Number (optional)</label>
              <input value={billingGST} onChange={e=>setBillingGST(e.target.value)} placeholder="e.g. 29ABCDE1234F1Z5" style={S.inp} />
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={S.lbl}>Billing Address</label>
            <input value={billingAddress} onChange={e=>setBillingAddress(e.target.value)} placeholder="Full billing address" style={S.inp} />
          </div>
          <div style={{marginBottom:12,maxWidth:280}}>
            <label style={S.lbl}>PAN (optional)</label>
            <input value={billingPAN} onChange={e=>setBillingPAN(e.target.value)} placeholder="e.g. ABCDE1234F" style={S.inp} />
          </div>
        </div>
        <div style={S.footer}>
          <button onClick={onClose} style={S.btnSec}>Cancel</button>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <span style={{fontSize:14,color:'#333'}}>
              Est: <b>₹{baseAmt.toFixed(2)}</b>
              <span style={{color:'#888',fontSize:12}}> + GST ₹{gstAmt.toFixed(2)}</span>
              {' = '}<b style={{color:'#2a3a5e',fontSize:16}}>₹{totalAmt.toFixed(2)}</b>
            </span>
            <button onClick={()=>setStep('review')} style={S.btnPri}>
              Proceed to Review →
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ─── REVIEW STEP ─────────────────────────────── */
  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <h2 style={S.hTitle}>🧾 Review & Pay</h2>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#fff',fontSize:22,cursor:'pointer'}}>✕</button>
        </div>
        <div style={S.body}>

          {/* Vendor card */}
          <div style={S.compCard}>
            <div style={{fontWeight:700,fontSize:15,color:'#2a3a5e',marginBottom:4}}>{COMPANY_NAME}</div>
            <div style={{color:'#555',marginBottom:4}}>{COMPANY_ADDRESS}</div>
            <span style={S.badge}>GST: {COMPANY_GST}</span>
            <span style={S.badge}>PAN: {COMPANY_PAN}</span>
            <div style={{marginTop:6,fontSize:12,color:'#666'}}>📧 {COMPANY_EMAIL} &nbsp;|&nbsp; 📞 {COMPANY_PHONE}</div>
          </div>

          {/* Bill To */}
          <div style={S.secHd}>Bill To</div>
          <div style={{fontSize:14,marginBottom:12}}>
            <strong>{billingName || organization}</strong>
            {billingAddress && <div style={{color:'#555',marginTop:2}}>{billingAddress}</div>}
            <div style={{marginTop:4}}>
              {billingGST && <span style={S.badge}>GST: {billingGST}</span>}
              {billingPAN && <span style={S.badge}>PAN: {billingPAN}</span>}
            </div>
            <div style={{marginTop:4,color:'#555',fontSize:13}}>📧 {email}</div>
          </div>

          {/* Pricing table */}
          <div style={S.secHd}>Subscription Summary</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:14}}>
            <tbody>
              {[
                ['Plan', plan],
                ['No. of Users', selectedUsers],
                ['Duration', `${selectedMonths} month(s)`],
                ['Subscription Period', `${formatDateDisplay(startDate)} → ${formatDateDisplay(endDate)}`],
                ['Per-user Rate', `₹${rateForUsers(selectedUsers)} / user / month`],
                ['Subtotal (excl. GST)', `₹${baseAmt.toFixed(2)}`],
                ['CGST @ 9%', `₹${(gstAmt/2).toFixed(2)}`],
                ['SGST @ 9%', `₹${(gstAmt/2).toFixed(2)}`],
              ].map(([k,v])=>(
                <tr key={k} style={{borderBottom:'1px solid #eee'}}>
                  <td style={{padding:'8px 4px',color:'#444'}}>{k}</td>
                  <td style={{padding:'8px 4px',textAlign:'right',fontWeight:600}}>{v}</td>
                </tr>
              ))}
              <tr style={{background:'#4777e8',color:'#fff'}}>
                <td style={{padding:'12px 8px',fontWeight:700,fontSize:15}}>Total Payable</td>
                <td style={{padding:'12px 8px',textAlign:'right',fontWeight:700,fontSize:18}}>₹{totalAmt.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{marginTop:10,fontSize:12,color:'#888'}}>
            * GST invoice will be sent to {email} after successful payment.
          </div>

          {/* Loading indicator shown while Razorpay popup is opening */}
          {submitting && (
            <div style={{marginTop:14,padding:'10px 14px',background:'#f0f4ff',border:'1px solid #c5d0f0',borderRadius:8,fontSize:13,color:'#4777e8',display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:18}}>⏳</span>
              Opening Razorpay payment window… please do not close this page.
            </div>
          )}
        </div>

        <div style={S.footer}>
          <button onClick={()=>setStep('form')} disabled={submitting} style={{...S.btnSec, opacity: submitting ? 0.5 : 1}}>
            ← Back
          </button>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#666'}}>
              <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                <rect width="32" height="32" rx="6" fill="#072654"/>
                <path d="M9 23L14.5 9H20L16 18H22L11.5 31L14 23H9Z" fill="#3395FF"/>
              </svg>
              Secured by Razorpay
            </div>
            <button
              onClick={handleConfirmPayment}
              disabled={submitting}
              style={submitting ? S.btnDis : S.btnPri}
            >
              {submitting
                ? '⏳ Opening Razorpay…'
                : `Pay ₹${totalAmt.toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionModal;